import { useState, useEffect, useCallback } from 'react';

interface TocItem {
  id: string;
  text: string;
  index: number;
}

interface ArticleTableOfContentsProps {
  items: TocItem[];
}

export default function ArticleTableOfContents({ items }: ArticleTableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleClick = useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 96;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (items.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          // Pick the topmost visible heading
          const topEntry = visible.reduce((a, b) =>
            a.boundingClientRect.top < b.boundingClientRect.top ? a : b
          );
          setActiveId(topEntry.target.id);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    items.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [items]);

  if (items.length < 2) return null;

  return (
    <aside className="hidden xl:block w-64 flex-shrink-0" aria-label="Article navigation">
      <div className="sticky top-24">
        <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
          {/* Header */}
          <button
            type="button"
            onClick={() => setIsCollapsed((v) => !v)}
            aria-expanded={!isCollapsed}
            aria-controls="toc-nav-list"
            className="w-full flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <i className="ri-list-check-2 text-teal-600 text-base w-5 h-5 flex items-center justify-center" aria-hidden="true"></i>
              <span className="text-sm font-bold text-gray-900 whitespace-nowrap">Table of Contents</span>
            </div>
            <i
              className={`ri-arrow-${isCollapsed ? 'down' : 'up'}-s-line text-gray-400 text-base w-5 h-5 flex items-center justify-center transition-transform`}
              aria-hidden="true"
            ></i>
          </button>

          {/* Items */}
          {!isCollapsed && (
            <nav id="toc-nav-list" aria-label="Table of contents" className="px-3 pb-4 max-h-[60vh] overflow-y-auto">
              <ol className="space-y-0.5">
                {items.map((item, i) => {
                  const isActive = activeId === item.id;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handleClick(item.id)}
                        aria-current={isActive ? 'true' : undefined}
                        aria-label={`Jump to section ${i + 1}: ${item.text}`}
                        className={`w-full text-left flex items-start gap-2.5 px-3 py-2 rounded-lg text-xs leading-snug transition-all cursor-pointer group ${
                          isActive
                            ? 'bg-teal-50 text-teal-700 font-semibold'
                            : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold mt-0.5 transition-colors ${
                            isActive
                              ? 'bg-teal-600 text-white'
                              : 'bg-gray-100 text-gray-400 group-hover:bg-gray-200'
                          }`}
                          aria-hidden="true"
                        >
                          {i + 1}
                        </span>
                        <span className="flex-1">{item.text}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
          )}

          {/* Progress bar */}
          {!isCollapsed && items.length > 0 && (
            <div className="px-5 pb-4" aria-hidden="true">
              <div className="flex items-center justify-between text-[10px] text-gray-400 mb-1.5">
                <span>Progress</span>
                <span>
                  {activeId
                    ? `${items.findIndex((i) => i.id === activeId) + 1} / ${items.length}`
                    : `0 / ${items.length}`}
                </span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-teal-500 rounded-full transition-all duration-300"
                  style={{
                    width: activeId
                      ? `${((items.findIndex((i) => i.id === activeId) + 1) / items.length) * 100}%`
                      : '0%',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Scroll to top of article"
            className="flex-1 flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-teal-600 bg-white border border-gray-100 rounded-lg py-2.5 transition-colors cursor-pointer hover:border-teal-200"
          >
            <i className="ri-arrow-up-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
            Top
          </button>
          <button
            type="button"
            onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
            aria-label="Scroll to bottom of article"
            className="flex-1 flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-teal-600 bg-white border border-gray-100 rounded-lg py-2.5 transition-colors cursor-pointer hover:border-teal-200"
          >
            <i className="ri-arrow-down-line w-3.5 h-3.5 flex items-center justify-center" aria-hidden="true"></i>
            Bottom
          </button>
        </div>
      </div>
    </aside>
  );
}

export type { TocItem };
